import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { getUuid } from '@sibe/utilities/common';
import {
  HsDefaultMaterial,
  HsGraphicCollection,
  HsGraphicLines,
  HsGraphicObject,
  HsGraphicPoints,
  HsMatrix4,
  HsPointsMaterial,
  HsQuaternion,
  HsRegularGeometryData,
  HsShaderData,
  HsShaderObjectType,
  hsGenerateCubeGeometryData,
  hsGenerateSphereGeometryData,
  hsIndexGeometry,
  hsMakeGeometry,
  hsMakeIndexedGeometry,
  type HsDefaultShader,
  type HsGeometryData,
  type HsGraphics,
  type HsHypersceneTypes,
  type HsHypersceneViewer,
  type HsIndexedGeometry,
  type HsMaterial,
  type HsRegularGeometry,
  type HsViewerPick,
} from '@sibe/hyperscene';
import { DemoViewerPage } from '../../classes/demo-viewer-page.class';
import { makeLitMaterial } from '../../functions/make-lit-material.function';

const CAMERA_POSITION: HsHypersceneTypes.Array3 = [2, 10, 30];
const GROUND_SIZE: number = 40;
const POINT_CLOUD_SIDE: number = 20;
const SPHERE_POSITION: HsHypersceneTypes.Array3 = [10, 7, 4];
const SPHERE_COLORS_COUNT: number = 12;
const HIGHLIGHT_COLOR: HsHypersceneTypes.Array3 = [1, 0.35, 0.35];

/** Builds a world matrix from a position and a scale. */
function makeMatrix(position: HsHypersceneTypes.Array3, scale: HsHypersceneTypes.Array3 = [1, 1, 1]): HsMatrix4 {
  return HsMatrix4.compose(position, HsQuaternion.makeIdentityQuaternion().values, scale);
}

/** Points of a unit sphere sampled on a `side`³ grid, colored by grid position unless `color` is given. */
function makeSpherePointCloud(
  graphics: HsGraphics,
  side: number,
  color: HsHypersceneTypes.Array3 | null = null
): HsRegularGeometry | null {
  const vertices: number[] = [];
  const colors: number[] = [];
  for (let i: number = 0; i < side; i++) {
    for (let j: number = 0; j < side; j++) {
      for (let k: number = 0; k < side; k++) {
        const x: number = (i / side) * 2 - 1;
        const y: number = (j / side) * 2 - 1;
        const z: number = (k / side) * 2 - 1;
        if (x * x + y * y + z * z < 1) {
          vertices.push(x, y, z);
          colors.push(...(color ?? [i / side, j / side, k / side]));
        }
      }
    }
  }
  return hsMakeGeometry(graphics, new HsRegularGeometryData(vertices, colors));
}

function makePointsMaterial(gl: WebGL2RenderingContext): HsPointsMaterial<HsDefaultShader> {
  const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
  shaderData.addColor().addDepth().addPoints();
  return new HsPointsMaterial(gl, shaderData.makeShader());
}

function makeFlatMaterial(gl: WebGL2RenderingContext, red: number, green: number, blue: number): HsMaterial {
  const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
  shaderData.addColor().addDepth();
  const material: HsMaterial = new HsDefaultMaterial(gl, shaderData.makeShader());
  material.color.set(red, green, blue);
  return material;
}

/**
 * Picking: the object under the pointer is highlighted and the footer shows the hit point, the object and its
 * kind. Meshes, instanced cubes, point clouds and lines share the scene.
 */
@Component({
  selector: 'hyperscene-raycast',
  templateUrl: './raycast.component.html',
  styleUrls: ['./raycast.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class RaycastDemo extends DemoViewerPage {
  public raycastInfoX: string = '';
  public raycastInfoY: string = '';
  public raycastInfoZ: string = '';
  public raycastInfoId: string = '';
  public raycastInfoType: string = '';

  private pointer: HsHypersceneTypes.Array2 | null = null;
  private pointerMoved: boolean = false;
  private highlighted: HsGraphicObject[] = [];
  private highlightMaterial: HsMaterial | null = null;
  private pulsingPoints: HsGraphicPoints | null = null;
  private ground: HsGraphicCollection | null = null;
  private groundMarker: HsGraphicObject | null = null;
  private readonly groundPositions: HsHypersceneTypes.Array3[] = [];
  /** Objects highlighted together when one of them is picked. */
  private readonly pickGroups: Map<HsGraphicObject, HsGraphicObject[]> = new Map();
  private readonly originalMaterials: Map<HsGraphicObject, HsMaterial> = new Map();
  private readonly listeners: (() => void)[] = [];

  protected initScene(viewer: HsHypersceneViewer): void {
    const graphics: HsGraphics = viewer.surface.graphics;
    const cubeData: HsGeometryData = hsGenerateCubeGeometryData();
    const cubeGeometry: HsRegularGeometry | null = hsMakeGeometry(graphics, cubeData);
    const indexedCubeGeometry: HsIndexedGeometry | null = hsMakeIndexedGeometry(graphics, hsIndexGeometry(cubeData));
    if (cubeGeometry === null || indexedCubeGeometry === null) {
      return;
    }
    this.highlightMaterial = makeLitMaterial(viewer.gl, ...HIGHLIGHT_COLOR);

    this.addObject(viewer, 'standard cube', cubeGeometry, makeLitMaterial(viewer.gl, 1, 1, 1), [-10, 2, 0], 2);
    this.addObject(viewer, 'indexed cube', indexedCubeGeometry, makeLitMaterial(viewer.gl, 1, 0.75, 0), [-15, 2, 0], 2);
    this.addGround(viewer, indexedCubeGeometry);
    this.addPointClouds(viewer);
    this.addColoredSphere(viewer);

    viewer.camera.fov = 1;
    viewer.camera.worldMatrix.setPosition(CAMERA_POSITION);
    viewer.camera.updateProjectionMatrix();
    viewer.onFrame(() => this.updatePick(viewer));
    this.listen(viewer.surface.canvas, 'mousemove', (event: MouseEvent) =>
      this.movePointer([event.clientX, event.clientY])
    );
    this.listen(viewer.surface.canvas, 'mouseleave', () => this.movePointer(null));
  }

  protected override animate(viewer: HsHypersceneViewer): boolean {
    const time: number = performance.now() * 0.001;
    this.pulsingPoints?.setPointsSize(viewer.gl, 20 + Math.pow(Math.sin(time) * 0.5 + 0.5, 2) * 10);
    return false;
  }

  public override ngOnDestroy(): void {
    this.listeners.forEach((remove: () => void) => remove());
    super.ngOnDestroy();
  }

  private addObject(
    viewer: HsHypersceneViewer,
    name: string,
    geometry: HsRegularGeometry | HsIndexedGeometry,
    material: HsMaterial,
    position: HsHypersceneTypes.Array3,
    scale: number
  ): HsGraphicObject {
    const object: HsGraphicObject = new HsGraphicObject(getUuid(), name, geometry, material);
    object.worldMatrix.copy(makeMatrix(position, [scale, scale, scale]));
    object.updateBoundingSphere();
    viewer.add(object);
    return object;
  }

  private addGround(viewer: HsHypersceneViewer, geometry: HsIndexedGeometry): void {
    const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.collection);
    shaderData.addColor().addDepth().addNormals().addDiffuseLighting();
    const material: HsMaterial = new HsDefaultMaterial(viewer.gl, shaderData.makeShader());
    const ground: HsGraphicCollection = new HsGraphicCollection(getUuid(), 'ground', geometry, material);
    for (let i: number = 0; i < GROUND_SIZE; i++) {
      for (let j: number = 0; j < GROUND_SIZE; j++) {
        const position: HsHypersceneTypes.Array3 = [i * 2 - GROUND_SIZE, -2, j * 2 - GROUND_SIZE];
        const shade: number = (i + j) % 2 === 1 ? 0.3 : 1;
        ground.addInstance(makeMatrix(position).values, [shade, shade, shade]);
        this.groundPositions.push(position);
      }
    }
    ground.updateCenter();
    ground.updateBoundingSphere();
    ground.updateBuffers(viewer.surface.graphics);
    viewer.add(ground);
    this.ground = ground;

    // A collection draws all its cubes with one material, so a picked cube is marked by a cube drawn over it.
    if (this.highlightMaterial !== null) {
      this.groundMarker = new HsGraphicObject(getUuid(), 'ground marker', geometry, this.highlightMaterial);
      this.groundMarker.visible = false;
      viewer.add(this.groundMarker);
    }
  }

  private addPointClouds(viewer: HsHypersceneViewer): void {
    const graphics: HsGraphics = viewer.surface.graphics;
    const coloredGeometry: HsRegularGeometry | null = makeSpherePointCloud(graphics, POINT_CLOUD_SIDE);
    const orangeGeometry: HsRegularGeometry | null = makeSpherePointCloud(graphics, POINT_CLOUD_SIDE, [1, 0.75, 0.25]);
    if (coloredGeometry === null || orangeGeometry === null) {
      return;
    }
    this.pulsingPoints = new HsGraphicPoints(getUuid(), 'point cloud', coloredGeometry, makePointsMaterial(viewer.gl));
    this.pulsingPoints.worldMatrix.copy(makeMatrix([0, 4, 0], [3.5, 3.5, 3.5]));
    this.pulsingPoints.updateBoundingSphere();
    viewer.add(this.pulsingPoints);

    const orangeMaterial: HsPointsMaterial<HsDefaultShader> = makePointsMaterial(viewer.gl);
    for (let i: number = 0; i < 10; i++) {
      const points: HsGraphicPoints = new HsGraphicPoints(
        getUuid(),
        `point cloud ${i}`,
        orangeGeometry,
        orangeMaterial
      );
      points.setPointsSize(viewer.gl, 8);
      points.worldMatrix.copy(makeMatrix([i * 5 - 25, 3, -25]));
      points.updateBoundingSphere();
      viewer.add(points);
    }
  }

  /**
   * A sphere whose face pairs get random colors, with a black wireframe over it. The current engine has no
   * per-face colors, so faces are split into a few single-color parts.
   */
  private addColoredSphere(viewer: HsHypersceneViewer): void {
    const graphics: HsGraphics = viewer.surface.graphics;
    const sphereData: HsGeometryData = hsGenerateSphereGeometryData(20);
    const parts: { vertices: number[]; normals: number[] }[] = Array.from({ length: SPHERE_COLORS_COUNT }, () => ({
      vertices: [],
      normals: [],
    }));
    const pairLength: number = 18;
    for (let start: number = 0; start < sphereData.vertices.length; start += pairLength) {
      const part: { vertices: number[]; normals: number[] } = parts[Math.floor(Math.random() * parts.length)];
      for (let index: number = start; index < Math.min(start + pairLength, sphereData.vertices.length); index++) {
        part.vertices.push(sphereData.vertices[index]);
        part.normals.push(sphereData.normals[index]);
      }
    }
    const group: HsGraphicObject[] = [];
    parts.forEach((part: { vertices: number[]; normals: number[] }, index: number) => {
      const geometry: HsRegularGeometry | null =
        part.vertices.length === 0
          ? null
          : hsMakeGeometry(graphics, new HsRegularGeometryData(part.vertices, part.normals));
      if (geometry !== null) {
        const material: HsMaterial = makeLitMaterial(viewer.gl, Math.random(), Math.random(), Math.random());
        group.push(this.addObject(viewer, `colored sphere ${index}`, geometry, material, SPHERE_POSITION, 5));
      }
    });

    const wireframeGeometry: HsRegularGeometry | null = hsMakeGeometry(
      graphics,
      new HsRegularGeometryData(sphereData.vertices, new Array<number>(sphereData.vertices.length).fill(0))
    );
    if (wireframeGeometry === null) {
      return;
    }
    const wireframe: HsGraphicLines = new HsGraphicLines(
      getUuid(),
      'sphere wireframe',
      wireframeGeometry,
      makeFlatMaterial(viewer.gl, 0, 0, 0)
    );
    wireframe.worldMatrix.copy(makeMatrix(SPHERE_POSITION, [5.007, 5.007, 5.007]));
    wireframe.updateBoundingSphere();
    viewer.add(wireframe);
    // Picking draws lines as triangles, so the wireframe covers the sphere; both highlight the sphere.
    [...group, wireframe].forEach((object: HsGraphicObject) => this.pickGroups.set(object, group));
  }

  private movePointer(pointer: HsHypersceneTypes.Array2 | null): void {
    this.pointer = pointer;
    this.pointerMoved = true;
  }

  /** Picks once per frame after the pointer moved; picking draws the visible objects into an ID buffer. */
  private updatePick(viewer: HsHypersceneViewer): void {
    if (!this.pointerMoved) {
      return;
    }
    this.pointerMoved = false;
    const pick: HsViewerPick | null = this.pointer === null ? null : viewer.pick(this.pointer[0], this.pointer[1]);
    if (pick !== null && pick.object === this.groundMarker) {
      return;
    }
    this.showPickInfo(pick);
    this.highlight(viewer, pick);
  }

  private showPickInfo(pick: HsViewerPick | null): void {
    this.raycastInfoX = pick?.point[0].toFixed(2) ?? '';
    this.raycastInfoY = pick?.point[1].toFixed(2) ?? '';
    this.raycastInfoZ = pick?.point[2].toFixed(2) ?? '';
    this.raycastInfoId = pick === null ? '' : pick.object.name;
    if (pick !== null && pick.instanceIndex !== null) {
      this.raycastInfoId += ` #${pick.instanceIndex}`;
    }
    this.raycastInfoType = pick === null ? '' : this.getObjectType(pick.object);
    this.changeDetector.markForCheck();
  }

  private getObjectType(object: HsGraphicObject): string {
    if (object instanceof HsGraphicCollection) {
      return 'Collection';
    }
    if (object instanceof HsGraphicPoints) {
      return 'Points';
    }
    if (object instanceof HsGraphicLines) {
      return 'Lines';
    }
    return 'Triangles';
  }

  private highlight(viewer: HsHypersceneViewer, pick: HsViewerPick | null): void {
    this.updateGroundMarker(viewer, pick);
    // Point clouds take their colors from the points, so a mesh material cannot highlight them.
    const objects: HsGraphicObject[] =
      pick === null || pick.object === this.ground || pick.object instanceof HsGraphicPoints
        ? []
        : this.pickGroups.get(pick.object) ?? [pick.object];
    if (objects[0] === this.highlighted[0] || this.highlightMaterial === null) {
      return;
    }
    const highlightMaterial: HsMaterial = this.highlightMaterial;
    this.highlighted.forEach((object: HsGraphicObject) => {
      object.material = this.originalMaterials.get(object) ?? object.material;
    });
    this.originalMaterials.clear();
    objects.forEach((object: HsGraphicObject) => {
      this.originalMaterials.set(object, object.material);
      object.material = highlightMaterial;
    });
    this.highlighted = objects;
    viewer.invalidate();
  }

  private updateGroundMarker(viewer: HsHypersceneViewer, pick: HsViewerPick | null): void {
    if (this.groundMarker === null) {
      return;
    }
    const position: HsHypersceneTypes.Array3 | undefined =
      pick !== null && pick.object === this.ground && pick.instanceIndex !== null
        ? this.groundPositions[pick.instanceIndex]
        : undefined;
    this.groundMarker.visible = position !== undefined;
    if (position !== undefined) {
      this.groundMarker.worldMatrix.copy(makeMatrix(position, [1.01, 1.01, 1.01]));
      this.groundMarker.updateBoundingSphere();
    }
    viewer.invalidate();
  }

  private listen<K extends keyof HTMLElementEventMap>(
    target: HTMLElement,
    type: K,
    listener: (event: HTMLElementEventMap[K]) => void
  ): void {
    target.addEventListener(type, listener);
    this.listeners.push(() => target.removeEventListener(type, listener));
  }
}
